import { Box, Heading, Text, Separator, Link, Grid } from "@radix-ui/themes";
import { MailIcon, InfoIcon } from "lucide-react";
import PageTemplate from "@/components/page-template/PageTemplate";

export default function HelpPage() {
  return (
    <PageTemplate
      title="Help & Support"
      description="Get assistance and important information about using the system"
    >
      <Box>
        {/* Beta Notice */}
        <Box
          p="4"
          mb="6"
          style={{
            border: "1px solid var(--blue-6)",
            borderRadius: "var(--radius-3)",
            backgroundColor: "var(--blue-2)",
          }}
        >
          <Box
            style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}
          >
            <InfoIcon size={20} color="var(--blue-9)" />
            <Box>
              <Heading as="h3" size="3" weight="medium" mb="2" color="blue">
                Beta Release
              </Heading>
              <Text color="blue" size="2" style={{ lineHeight: "1.5" }}>
                This platform is currently in beta as we actively develop and
                refine features. While the system is fully functional and ready
                for use, please be aware that occasional updates may introduce
                changes. We're committed to providing a stable experience and
                will communicate any significant updates in advance. Your
                feedback helps us build a better product!
              </Text>
            </Box>
          </Box>
        </Box>

        {/* Contact Information */}
        <Grid columns={{ initial: "1", md: "2" }} gap="6" mb="6">
          <Box
            p="5"
            style={{
              border: "1px solid var(--gray-5)",
              borderRadius: "var(--radius-3)",
            }}
          >
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "16px",
              }}
            >
              <MailIcon size={24} color="var(--blue-9)" />
              <Heading as="h2" size="4" weight="medium">
                Get in Touch
              </Heading>
            </Box>
            <Text color="gray" mb="3" style={{ lineHeight: "1.6" }}>
              Need help or encountered an issue? We're here to assist you. Send
              us an email with details about your problem or question.
            </Text>
            <Box>
              <Text size="2" color="gray" mb="2">
                Email us at:
              </Text>
              <Link
                href="mailto:support@anythink.uk"
                size="3"
                weight="medium"
                style={{ display: "block", marginBottom: "12px" }}
              >
                support@anythink.uk
              </Link>
              <Text size="1" color="gray" style={{ lineHeight: "1.4" }}>
                Please include relevant details such as error messages, steps to
                reproduce the issue, and your account information to help us
                assist you more effectively.
              </Text>
            </Box>
          </Box>

          <Box
            p="5"
            style={{
              border: "1px solid var(--gray-5)",
              borderRadius: "var(--radius-3)",
            }}
          >
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "16px",
              }}
            >
              <InfoIcon size={24} color="var(--green-9)" />
              <Heading as="h2" size="4" weight="medium">
                What to Include
              </Heading>
            </Box>
            <Text color="gray" mb="3" style={{ lineHeight: "1.6" }}>
              To help us resolve your issue quickly, please include:
            </Text>
            <Box style={{ paddingLeft: "16px", paddingTop: "8px" }}>
              <Text size="2" color="gray" mb="3" style={{ display: "block" }}>
                • A clear description of the problem
              </Text>
              <Text size="2" color="gray" mb="3" style={{ display: "block" }}>
                • Steps to reproduce the issue
              </Text>
              <Text size="2" color="gray" mb="3" style={{ display: "block" }}>
                • Any error messages you received
              </Text>
              <Text size="2" color="gray" mb="3" style={{ display: "block" }}>
                • Browser and operating system details
              </Text>
              <Text size="2" color="gray" style={{ display: "block" }}>
                • Screenshots if applicable
              </Text>
            </Box>
          </Box>
        </Grid>

        <Separator size="4" mb="6" />

        {/* Additional Information */}
        <Box>
          <Heading as="h2" size="4" weight="medium" mb="4">
            Important Information
          </Heading>
          <Grid columns={{ initial: "1", lg: "2" }} gap="4">
            <Box>
              <Heading as="h3" size="3" weight="medium" mb="2">
                Response Time
              </Heading>
              <Text color="gray" size="2" mb="4" style={{ lineHeight: "1.5" }}>
                We aim to respond to all support requests within 24-48 hours
                during business days. Critical issues may receive faster
                attention.
              </Text>
            </Box>
            <Box>
              <Heading as="h3" size="3" weight="medium" mb="2">
                Data Security
              </Heading>
              <Text color="gray" size="2" mb="4" style={{ lineHeight: "1.5" }}>
                We take data security seriously and implement industry-standard
                protections. As we continue refining the platform, we recommend
                starting with non-critical data while you evaluate the system.
              </Text>
            </Box>

            <Box>
              <Heading as="h3" size="3" weight="medium" mb="2">
                Continuous Improvement
              </Heading>
              <Text color="gray" size="2" mb="4" style={{ lineHeight: "1.5" }}>
                We're continuously adding features and improvements. We'll keep
                you informed of new capabilities and any important changes as
                they roll out.
              </Text>
            </Box>
            <Box>
              <Heading as="h3" size="3" weight="medium" mb="2">
                Feedback Welcome
              </Heading>
              <Text color="gray" size="2" style={{ lineHeight: "1.5" }}>
                Your feedback helps us improve! Don't hesitate to share
                suggestions, feature requests, or general comments about your
                experience.
              </Text>
            </Box>
          </Grid>
        </Box>
      </Box>
    </PageTemplate>
  );
}
